import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Get the API URL from environment variable or fallback to localhost for development
const API_URL = process.env.REACT_APP_API_URL || 
                (window.location.hostname === 'localhost' 
                  ? 'http://localhost:5000' 
                  : 'https://isdestichtinggezond-nl.onrender.com');

const PigletGame = ({ onClose }) => {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [pigletPosition, setPigletPosition] = useState({ top: 50, left: 50 });
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('pigletGameHighScore');
    return saved !== null ? parseInt(saved) : 0;
  });
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [playerName, setPlayerName] = useState(() => {
    const savedName = localStorage.getItem('pigletGamePlayerName');
    return savedName || '';
  });
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [submittingScore, setSubmittingScore] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const gameAreaRef = useRef(null);
  const timerRef = useRef(null);
  const pigletRef = useRef(null);
  const nameInputRef = useRef(null);

  // Fetch leaderboard data
  const fetchLeaderboard = async () => {
    try {
      setIsLoadingLeaderboard(true);
      setLeaderboardError(null);
      
      console.log(`Fetching leaderboard from: ${API_URL}/api/leaderboard`);
      const response = await axios.get(`${API_URL}/api/leaderboard`);
      console.log('Leaderboard response:', response.data);
      
      // Validate response format
      if (Array.isArray(response.data)) {
        setLeaderboard(response.data);
      } else {
        console.error('Invalid leaderboard data format:', response.data);
        setLeaderboardError('Ongeldige data ontvangen van de server.');
        // Try to use cached leaderboard if available
        const cached = localStorage.getItem('cachedLeaderboard');
        if (cached) {
          try {
            setLeaderboard(JSON.parse(cached));
          } catch (e) {
            // Ignore parse errors
          }
        }
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLeaderboardError('Kon de ranglijst niet laden. Probeer het later opnieuw.');
      
      // Try to use cached leaderboard if available
      const cached = localStorage.getItem('cachedLeaderboard');
      if (cached) {
        try {
          setLeaderboard(JSON.parse(cached));
        } catch (e) {
          // Ignore parse errors
        }
      }
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  // Cache leaderboard when it changes
  useEffect(() => {
    if (leaderboard && leaderboard.length > 0) {
      localStorage.setItem('cachedLeaderboard', JSON.stringify(leaderboard));
    }
  }, [leaderboard]);

  // Fetch leaderboard on initial load
  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const startGame = () => {
    setGameStarted(true);
    setGameEnded(false);
    setScore(0);
    setTimeLeft(30);
    setShowLeaderboard(false);
    setCurrentEntry(null);
    movePiglet();
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endGame = () => {
    setGameEnded(true);
    clearInterval(timerRef.current);
    
    // Update local high score
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('pigletGameHighScore', score.toString());
    }
    
    // Focus name input if name not set
    setTimeout(() => {
      if (nameInputRef.current && !playerName) {
        nameInputRef.current.focus();
      }
    }, 100);
  };

  const saveScore = async () => {
    // Save player name to localStorage for convenience
    if (playerName) {
      localStorage.setItem('pigletGamePlayerName', playerName);
    }
    
    // Don't submit if score is 0
    if (score === 0) {
      setShowLeaderboard(true);
      return;
    }

    try {
      setSubmittingScore(true);
      
      // Create the score entry
      const scoreData = {
        name: playerName || 'Anonieme Knor',
        score: score
      };
      
      console.log('Submitting score:', scoreData);
      
      // Submit score to server
      const response = await axios.post(
        `${API_URL}/api/leaderboard`, 
        scoreData,
        { timeout: 8000 } // 8 seconds timeout
      );
      
      console.log('Server response:', response.data);
      
      if (response.data && Array.isArray(response.data.leaderboard)) {
        // Update leaderboard with response data
        setLeaderboard(response.data.leaderboard || []);
        setCurrentEntry(response.data.entry);
      } else if (response.data && response.data.entry) {
        // We got the entry back but not the leaderboard
        setCurrentEntry(response.data.entry);
        // Refresh the leaderboard
        fetchLeaderboard();
      } else {
        throw new Error('Invalid response format');
      }
      
      setShowLeaderboard(true);
    } catch (error) {
      console.error('Error saving score:', error);
      
      // Create a fallback entry
      const fallbackEntry = {
        name: playerName || 'Anonieme Knor',
        score: score,
        date: new Date().toISOString()
      };
      
      setCurrentEntry(fallbackEntry);
      
      // Try to add to local leaderboard
      try {
        const updatedLeaderboard = [...leaderboard, fallbackEntry]
          .sort((a, b) => b.score - a.score)
          .slice(0, 10);
        
        setLeaderboard(updatedLeaderboard);
        localStorage.setItem('cachedLeaderboard', JSON.stringify(updatedLeaderboard));
      } catch (e) {
        console.error('Failed to update local leaderboard:', e);
      }
      
      setShowLeaderboard(true);
    } finally {
      setSubmittingScore(false);
    }
  };

  const toggleLeaderboard = async () => {
    // If we're about to show the leaderboard, refresh data from server
    if (!showLeaderboard) {
      await fetchLeaderboard();
    }
    setShowLeaderboard(!showLeaderboard);
  };

  // Debug function to test server connection
  const checkServerConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/debug`);
      console.log('Server debug info:', response.data);
      return response.data;
    } catch (error) {
      console.error('Server connection test failed:', error);
      return null;
    }
  };

  // Add a check when the game loads
  useEffect(() => {
    checkServerConnection();
  }, []);

  const restartGame = () => {
    clearInterval(timerRef.current);
    startGame();
  };

  const handlePigletClick = () => {
    setScore(score + 1);
    movePiglet();
  };

  const movePiglet = () => {
    if (!gameAreaRef.current) return;
    
    const gameWidth = gameAreaRef.current.offsetWidth - (isMobile ? 40 : 60);
    const gameHeight = gameAreaRef.current.offsetHeight - (isMobile ? 40 : 60);
    
    setPigletPosition({
      top: Math.floor(Math.random() * gameHeight),
      left: Math.floor(Math.random() * gameWidth)
    });
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="game-modal">
      <div className="game-container">
        <div className="game-header">
          <h2>Knorion Smash!</h2>
          <div className="game-stats">
            <span>Score: {score}</span>
            <span>Tijd: {timeLeft}s</span>
            <span>Record: {highScore}</span>
          </div>
        </div>
        
        {!gameStarted && !gameEnded && timeLeft === 30 ? (
          <div className="game-start">
            <p>Smash zo veel mogelijk Orion knorren om punten te verzamelen!</p>
            <button onClick={startGame}>Start Spel</button>
            <button onClick={toggleLeaderboard} className="leaderboard-btn">
              {showLeaderboard ? "Verberg Ranglijst" : "Toon Ranglijst"}
            </button>
            
            {showLeaderboard && (
              <div className="leaderboard start-leaderboard">
                <h3>Beste Knor-Smashers</h3>
                {isLoadingLeaderboard ? (
                  <p className="loading-message">Ranglijst laden...</p>
                ) : leaderboardError ? (
                  <p className="error-message">{leaderboardError}</p>
                ) : leaderboard.length === 0 ? (
                  <p className="empty-message">Nog geen scores. Wees de eerste!</p>
                ) : (
                  <div className="leaderboard-list">
                    <div className="leaderboard-header">
                      <span className="rank">#</span>
                      <span className="name">Speler</span>
                      <span className="score">Score</span>
                      <span className="date">Datum</span>
                    </div>
                    {leaderboard.map((entry, index) => (
                      <div key={index} className="leaderboard-item">
                        <span className="rank">{index + 1}</span>
                        <span className="name">{entry.name}</span>
                        <span className="score">{entry.score}</span>
                        <span className="date">{formatDate(entry.date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : gameEnded ? (
          showLeaderboard ? (
            <div className="leaderboard">
              <h3>Ranglijst - Beste Smashers</h3>
              {isLoadingLeaderboard ? (
                <p className="loading-message">Ranglijst laden...</p>
              ) : leaderboardError ? (
                <p className="error-message">{leaderboardError}</p>
              ) : (
                <div className="leaderboard-list">
                  <div className="leaderboard-header">
                    <span className="rank">#</span>
                    <span className="name">Speler</span>
                    <span className="score">Score</span>
                    <span className="date">Datum</span>
                  </div>
                  {leaderboard.map((entry, index) => (
                    <div 
                      key={index} 
                      className={`leaderboard-item ${
                        currentEntry && 
                        entry.name === currentEntry.name && 
                        entry.score === currentEntry.score ? 'current' : ''
                      }`}
                    >
                      <span className="rank">{index + 1}</span>
                      <span className="name">{entry.name}</span>
                      <span className="score">{entry.score}</span>
                      <span className="date">{formatDate(entry.date)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="leaderboard-buttons">
                <button onClick={restartGame}>Opnieuw Spelen</button>
                <button onClick={() => setShowLeaderboard(false)}>Terug</button>
              </div>
            </div>
          ) : (
            <div className="game-over">
              <h3>Spel Afgelopen!</h3>
              <p>Je score: {score} biggetjes gesmasht</p>
              {score > highScore - 1 && <p className="new-record">Nieuw Record!</p>}
              
              <div className="name-input-container">
                <label htmlFor="playerName">Je naam:</label>
                <input
                  type="text"
                  id="playerName"
                  ref={nameInputRef}
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Vul je naam in"
                  maxLength={15}
                />
              </div>
              
              <div className="game-over-buttons">
                <button 
                  onClick={saveScore} 
                  disabled={submittingScore}
                  className={submittingScore ? "submitting" : ""}
                >
                  {submittingScore ? "Opslaan..." : "Opslaan & Toon Ranglijst"}
                </button>
                <button onClick={restartGame}>Opnieuw Spelen</button>
              </div>
            </div>
          )
        ) : (
          <div className="game-area" ref={gameAreaRef}>
            <div 
              className="piglet" 
              ref={pigletRef}
              style={{ 
                top: pigletPosition.top + 'px', 
                left: pigletPosition.left + 'px',
                width: isMobile ? '50px' : '60px',
                height: isMobile ? '50px' : '60px'
              }}
              onClick={handlePigletClick}
            />
          </div>
        )}
        
        <button className="close-game" onClick={onClose}>Spel Sluiten</button>
      </div>
    </div>
  );
};

export default PigletGame; 